import { css } from "@emotion/css";
import { buttonBaseClasses, formHelperTextClasses, iconButtonClasses, inputBaseClasses, inputLabelClasses, textFieldClasses, typographyClasses } from "@mui/material";
import type { PickersDayClasses } from "@mui/x-date-pickers";
import { LocalizationProvider, DatePicker as MaterialDatePicker, pickersDayClasses, pickersLayoutClasses, pickersYearClasses } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { text, themeTokens } from "@octopusdeploy/design-system-tokens";
import * as React from "react";
export interface DatePickerProps {
    /**
     * A boolean property which controls whether the date picker is disabled.
     */
    disabled?: boolean;
    /**
     * The label to use for the date picker.
     */
    label?: string;
    /**
     * An optional error string to display below the date picker.
     */
    error?: string;
    /**
     * An optional name to use for the date picker.
     */
    name?: string;
    /**
     * The minimum date available as a selection in the date picker. Any dates before this
     * will not be selectable.
     */
    minDate?: Date;
    /**
     * The date format to use when displaying the date.
     * @default "MMM dd, yyyy"
     * @remarks please see https://date-fns.org/v2.30.0/docs/format for available formatting options.
     */
    format?: string;
    /**
     * The current date value to use for the picker.
     */
    value: Date | null;
    /**
     * The change callback handler
     * @description
     * The onChange callback is fired whenever the date is changed and is guaranteed to be a valid
     * date.
     */
    onChange: (newValue: Date) => void;
    /**
     * A note to show below the date picker
     */
    note?: string;
    /**
     * Whether the date input should take up the full width of its container
     */
    fullWidth?: boolean;
}
export function DatePicker({ value, label, error, name, disabled, format, minDate, onChange, note, fullWidth }: DatePickerProps) {
    const latestChangeHandler = React.useRef(onChange);
    const handleChange = React.useCallback((value: Date | null) => {
        if (value) {
            latestChangeHandler.current?.(value);
        }
    }, []);
    return (<LocalizationProvider dateAdapter={AdapterDateFns}>
            {/* We need some sort of parent underneath the PickersUtils provider, otherwise we get an error being logged
     when the date picker is disabled.*/}
            <MaterialDatePicker slotProps={{
            day: {
                classes: dayStyles,
                disableRipple: true,
            },
            layout: {
                classes: layoutStyles,
            },
            textField: {
                classes: textFieldStyles,
                variant: "standard",
                error: !!error,
                helperText: error || note,
                fullWidth,
            },
            openPickerButton: { ...(label ? { "aria-label": label } : {}), disableRipple: true },
            switchViewButton: { classes: switchViewButtonStyles, disableRipple: true },
            yearButton: { className: yearButtonStyles },
            nextIconButton: { classes: nextIconButtonStyles, disableRipple: true },
            previousIconButton: { classes: previousIconButtonStyles, disableRipple: true },
        }} name={name} format={format} value={value} onChange={handleChange} disabled={disabled} minDate={minDate} label={label}/>
        </LocalizationProvider>);
}
const dayStyles: Partial<PickersDayClasses> = {
    root: css({
        [`&.${pickersDayClasses.root}`]: {
            color: themeTokens.color.text.primary,
            "&:hover": {
                backgroundColor: themeTokens.color.button.background.quiet.hover,
                color: themeTokens.color.button.text.quiet,
            },
            "&:focus-visible": {
                outline: `solid 1px ${themeTokens.color.border.selected}`,
            },
        },
    }),
    today: css({
        [`&.${pickersDayClasses.today}`]: {
            color: themeTokens.color.text.primary,
            border: `solid 1px ${themeTokens.color.border.tertiary}`,
        },
        [`&.${pickersDayClasses.today}:not(${pickersDayClasses.selected})`]: {
            borderColor: themeTokens.color.border.tertiary,
        },
    }),
    selected: css({
        [`&.${pickersDayClasses.selected}.${pickersDayClasses.today}.${buttonBaseClasses.root}`]: {
            color: themeTokens.color.text.inverse.primary,
            backgroundColor: themeTokens.color.background.selected,
            "&:hover": {
                backgroundColor: themeTokens.color.button.background.quiet.hover,
                color: themeTokens.color.button.text.quiet,
            },
        },
        [`&.${pickersDayClasses.root}.${pickersDayClasses.selected}.${buttonBaseClasses.root}`]: {
            color: themeTokens.color.text.inverse.primary,
            backgroundColor: themeTokens.color.background.selected,
            "&:hover": {
                backgroundColor: themeTokens.color.button.background.quiet.hover,
                color: themeTokens.color.button.text.quiet,
            },
            "&:focus-visible": {
                boxShadow: themeTokens.shadow.focused,
            },
        },
    }),
};
const yearButtonStyles = css({
    [`&.${pickersYearClasses.yearButton}`]: {
        [`&.${pickersYearClasses.selected}`]: {
            backgroundColor: themeTokens.color.background.selected,
            color: themeTokens.color.text.inverse.primary,
            "&:hover": {
                backgroundColor: themeTokens.color.button.background.quiet.hover,
            },
            ":focus-visible": {
                boxShadow: themeTokens.shadow.focused,
            },
        },
        [`&:focus-visible`]: {
            outline: `solid 1px ${themeTokens.color.border.selected}`,
            backgroundColor: themeTokens.color.button.background.quiet.hover,
        },
        [`&:hover`]: {
            backgroundColor: themeTokens.color.button.background.quiet.hover,
            color: themeTokens.color.button.text.quiet,
        },
    },
});
const layoutStyles: Partial<typeof pickersLayoutClasses> = {
    root: css({
        //Targets the year selector
        [`&.${pickersLayoutClasses.root}`]: {
            color: themeTokens.color.text.primary,
            background: themeTokens.color.background.primary.default,
        },
        //Targets the column headings for each day
        [`&.${pickersLayoutClasses.root} .${typographyClasses.root}`]: {
            color: themeTokens.color.text.secondary,
        },
    }),
};
const baseIconButtonStyles: Partial<typeof iconButtonClasses> = {
    root: css({
        [`&.${iconButtonClasses.root}`]: {
            color: themeTokens.color.text.info,
            [`&.${iconButtonClasses.disabled}`]: { color: themeTokens.color.button.icon.disabled },
            "&:hover, &:focus-visible": {
                backgroundColor: themeTokens.color.button.background.quiet.hover,
            },
        },
    }),
};
const switchViewButtonStyles = baseIconButtonStyles;
const nextIconButtonStyles = baseIconButtonStyles;
const previousIconButtonStyles = baseIconButtonStyles;
const textFieldStyles: Partial<typeof textFieldClasses> = {
    root: css({
        [`&.${textFieldClasses.root}`]: {
            [`.${inputLabelClasses.root}`]: {
                color: themeTokens.color.textField.text.primary,
                [`&.${inputLabelClasses.focused}`]: { color: themeTokens.color.textField.text.selected },
                [`&.${inputLabelClasses.error}`]: {
                    color: themeTokens.color.textField.text.danger,
                },
                [`&.${inputLabelClasses.disabled}`]: { color: themeTokens.color.textField.text.disabled },
            },
            [`.${inputBaseClasses.root}`]: {
                //Field bottom border colours
                "::before": {
                    borderBottom: `1px solid ${themeTokens.color.textField.border.default}`,
                },
                [`&.${inputBaseClasses.focused}:not(.${inputBaseClasses.error}, .${inputBaseClasses.disabled})::after`]: {
                    borderBottom: `2px solid ${themeTokens.color.textField.border.active}`,
                },
                [`&.${inputBaseClasses.error}::after`]: {
                    borderBottom: `2px solid ${themeTokens.color.textField.border.danger}`,
                },
                [`&:hover:not(.${inputBaseClasses.error}, .${inputBaseClasses.disabled})::before`]: {
                    borderBottom: `2px solid ${themeTokens.color.textField.border.hover}`,
                },
                [`&.${inputBaseClasses.disabled}::before`]: { borderBottom: `1px solid ${themeTokens.color.textField.border.disabled}` },
            },
            [`.${inputBaseClasses.input}`]: {
                color: themeTokens.color.textField.text.primary,
                font: text.body.regular.medium,
                [`&.${inputBaseClasses.disabled}`]: { color: themeTokens.color.textField.text.disabled },
            },
            //Icon
            [`.${inputBaseClasses.adornedEnd} .${iconButtonClasses.root}`]: {
                color: themeTokens.color.icon.tertiary,
                [`&.${inputBaseClasses.disabled}`]: { color: themeTokens.color.textField.icon.disabled },
            },
            //Notes and Error text
            [`.${formHelperTextClasses.root}`]: {
                font: text.body.regular.xSmall,
                color: themeTokens.color.textField.text.secondary,
                [`&.${formHelperTextClasses.disabled}`]: { color: themeTokens.color.textField.text.disabled },
            },
            [`.${formHelperTextClasses.error}`]: {
                color: themeTokens.color.textField.text.danger,
            },
        },
    }),
};
